import React, { useContext } from 'react';
import { StudioPrintSettingsContext, useDebounceToGlobal, UserContext } from 'venn-components';
import styled, { ThemeContext } from 'styled-components';
import { CheckboxWrapper, getAppTitle, Headline3, Tooltip, TooltipBodyDirection } from 'venn-ui-kit';
import {
  CUSTOM_FOOTER_MAX_LENGTH,
  DelimitedWrapper,
  Field,
  GapWrapper,
  getHideToggleConfig,
  Img,
  ImgWrapper,
  InfoIcon,
  InputField,
  StyledLabel,
} from '../shared';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  analysisViewNameState,
  studioCustomFooterText,
  studioHideFooterDisclaimer,
  studioHideFooterPagination,
  studioHideHeaderCompanyName,
  studioHideHeaderLogo,
  studioHideHeaderViewName,
} from 'venn-state';
import { useHasFF } from 'venn-utils';

const PageSection = () => {
  const hasCustomizationEnhancements = useHasFF('rl_customization_enhancements_ff');
  const { profileSettings, hasPermission } = useContext(UserContext);
  const { defaultLogoId, organizationPrintSettings } = useContext(StudioPrintSettingsContext);
  const hideHeaderLogoState = useRecoilState(studioHideHeaderLogo);
  const hideHeaderCompanyNameState = useRecoilState(studioHideHeaderCompanyName);
  const hideHeaderViewNameState = useRecoilState(studioHideHeaderViewName);
  const hideFooterDisclaimerState = useRecoilState(studioHideFooterDisclaimer);
  const hideFooterPaginationState = useRecoilState(studioHideFooterPagination);
  const [customFooterText, setCustomFooterText] = useRecoilState(studioCustomFooterText);
  const [footerText, setFooterText] = useDebounceToGlobal(customFooterText ?? '', (values) => {
    setCustomFooterText(values);
  });
  const viewName = useRecoilValue(analysisViewNameState);
  const theme = useContext(ThemeContext);

  const canCreateTemplate = hasPermission('STUDIO_CREATE_TEMPLATE');

  const headerSection = (
    <>
      <StyledLabel>Header</StyledLabel>
      <CheckboxWrapper
        {...getHideToggleConfig(hideHeaderLogoState)}
        disabled={!canCreateTemplate}
        inputColor={hasCustomizationEnhancements ? theme.Colors.DarkBlue : undefined}
      >
        <Field>
          Company Logo
          <Tooltip content="Logo can be updated in Settings" bodyDirection={TooltipBodyDirection.Right}>
            <InfoIcon
              type="info-circle"
              prefix="far"
              customColor={hasCustomizationEnhancements ? theme.Colors.GreyScale.Grey60 : undefined}
            />
          </Tooltip>
          {hasCustomizationEnhancements ? '' : ':'}
          {defaultLogoId && (
            <ImgWrapper>
              <Img src={`/api/printing/logo/${defaultLogoId}`} />
            </ImgWrapper>
          )}
        </Field>
      </CheckboxWrapper>
      <CheckboxWrapper
        {...getHideToggleConfig(hideHeaderCompanyNameState)}
        disabled={!canCreateTemplate}
        inputColor={hasCustomizationEnhancements ? theme.Colors.DarkBlue : undefined}
      >
        <strong>Company Name{hasCustomizationEnhancements ? '' : ':'} </strong>{' '}
        <span className={hasCustomizationEnhancements ? '!text-venn-grey-80' : ''}>
          {organizationPrintSettings?.organizationName ?? profileSettings?.organization?.name}
        </span>
      </CheckboxWrapper>
      <CheckboxWrapper
        {...getHideToggleConfig(hideHeaderViewNameState)}
        disabled={!canCreateTemplate}
        inputColor={hasCustomizationEnhancements ? theme.Colors.DarkBlue : undefined}
      >
        <strong>View Name{hasCustomizationEnhancements ? '' : ':'} </strong>{' '}
        <span className={hasCustomizationEnhancements ? '!text-venn-grey-80' : ''}>{viewName ?? 'Unsaved View'}</span>
      </CheckboxWrapper>
    </>
  );

  const Wrapper = hasCustomizationEnhancements ? DelimitedWrapper : RegularWrapper;
  const wrappedHeaderSection = hasCustomizationEnhancements ? headerSection : <GapWrapper>{headerSection}</GapWrapper>;

  return (
    <Wrapper>
      {hasCustomizationEnhancements && <Headline3>Page Settings</Headline3>}
      {wrappedHeaderSection}

      <GapWrapper>
        <StyledLabel>Footer</StyledLabel>
        <div className={`flex flex-wrap [&>label]:min-w-[50%] ${hasCustomizationEnhancements && 'flex-col'}`}>
          <CheckboxWrapper
            {...getHideToggleConfig(hideFooterDisclaimerState)}
            disabled={!canCreateTemplate}
            inputColor={hasCustomizationEnhancements ? theme.Colors.DarkBlue : undefined}
          >
            <strong> Disclaimer</strong>
            <Tooltip
              content={`Appears on bottom of every page in the report in addition to ${getAppTitle()} disclaimers`}
              bodyDirection={TooltipBodyDirection.Right}
            >
              <InfoIcon
                type="info-circle"
                prefix="far"
                className={hasCustomizationEnhancements ? '!text-venn-grey-60' : ''}
              />
            </Tooltip>
          </CheckboxWrapper>
          <CheckboxWrapper
            {...getHideToggleConfig(hideFooterPaginationState)}
            disabled={!canCreateTemplate}
            inputColor={hasCustomizationEnhancements ? theme.Colors.DarkBlue : undefined}
          >
            <strong>Page Number</strong>
          </CheckboxWrapper>
        </div>
        <StyledLabel htmlFor="custom-text">
          {' '}
          Custom Footer Text{hasCustomizationEnhancements ? '' : ': '}{' '}
          <Tooltip
            content={`Appears at end of every report in addition to ${getAppTitle()} disclosures`}
            bodyDirection={TooltipBodyDirection.Right}
          >
            <InfoIcon
              type="info-circle"
              prefix="far"
              className={hasCustomizationEnhancements ? '!text-venn-grey-60' : ''}
            />
          </Tooltip>
        </StyledLabel>
        <InputField
          placeholder="Enter Text"
          value={footerText}
          name="Prepared For"
          id="custom-text"
          onChange={setFooterText}
          charactersLimit={CUSTOM_FOOTER_MAX_LENGTH}
          disabled={!canCreateTemplate}
          textInputStyle={!canCreateTemplate ? { backgroundColor: theme.Colors.GreyScale.Grey40 } : undefined}
        />
      </GapWrapper>
    </Wrapper>
  );
};

export default PageSection;

const RegularWrapper = styled.div`
  label {
    justify-content: flex-start;
  }
`;
